import { Button, IconButton, Typography, useTheme } from "@mui/material";
import { Box, Container } from "@mui/system";
import { FancyIcon, InfoIcon, PowerFilled } from "@WahooFitness/redesignr";
import { t, Trans } from "@lingui/macro";
import SelectComponent from "@/components/Quiz/4dpQuiz/PowerZoneUpdate/SelectComponent";
import usePowerZoneUpdate from "@/components/Quiz/4dpQuiz/PowerZoneUpdate/usePowerZoneUpdate";
import { FourDPQuizResultsType } from "@WahooFitness/cloud-client-types";
import Loader from "@/components/Quiz/Results/Loader";
import { useEffect, useState } from "react";
import ZoneViewComponent from "@/components/Quiz/4dpQuiz/PowerZoneUpdate/ZoneViewComponent";
import { SourcePage } from "@/services/Analytics/EventTypes";
import { ErrorBoundary } from "@WahooFitness/wahoo-offline-mfe";

interface Props {
  results?:
    | FourDPQuizResultsType
    | { profile: { fourdp: { FTP: number; AC: number; MAP: number; NM: number } } };
  closeOnBack?: boolean;
  sourcePage?: SourcePage;
  onClose?: () => void;
}

const PowerZoneUpdate = ({ results, closeOnBack, sourcePage, onClose }: Props) => {
  const { palette } = useTheme();
  const {
    selectedZone,
    onChange,
    options,
    hasMultipleZoneSets,
    savedZoneSets,
    handleZoneInfoClick,
    handleSave,
    zoneKeys,
    calculated4DPPowerZonesLoading,
    calculated4DPPowerZonesError,
    displayCalculated4DPPowerZones,
  } = usePowerZoneUpdate({ results, closeOnBack, sourcePage, onClose });

  const [loadingDelay, setLoadingDelay] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingDelay(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
      <ErrorBoundary
        isLoading={calculated4DPPowerZonesLoading || loadingDelay}
        error={calculated4DPPowerZonesError}
        loadingComponent={<Loader message={t`Calculating your power zones...`} />}
      >
        <Box
          sx={{
            position: "relative",
            height: "100dvh",
            backgroundColor: palette.background.level1,
            // set by the shared shell; keeps the zone-update buttons clear of the native tab bar
            paddingBottom: "calc(var(--wf-bottom-safe-area, 0px) + 16px)",
          }}
          display="flex"
          flexDirection="column"
          p={2}
          pt={1.5}
          alignItems="center"
          overflow="scroll"
          maxWidth="sm"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            p={1.5}
            pb={2}
          >
            <FancyIcon
              icon={<PowerFilled sx={{ color: palette.data.power.main }} />}
              size="medium"
              color="warning"
            />
            <Typography
              variant="ui-lg-medium"
              color={palette.text.primary}
              textAlign="center"
              mt={1}
            >
              <Trans>Recommended power zones based on your Athlete Profile.</Trans>
            </Typography>
          </Box>
          <Box bgcolor={palette.secondary.main} borderRadius="8px" px={2} width="100%">
            <ZoneViewComponent zones={displayCalculated4DPPowerZones ?? []} />
          </Box>
          <>
            <Box
              display="flex"
              flexDirection="row"
              gap={1}
              justifyContent="space-between"
              width="100%"
              mt={3}
              alignItems="center"
            >
              <Typography variant="ui-md-medium" color={palette.text.primary}>
                {hasMultipleZoneSets ? (
                  <Trans>Which power zones set do you want to update?</Trans>
                ) : (
                  <Trans>Update your power zones?</Trans>
                )}
              </Typography>
              {!hasMultipleZoneSets && (
                <IconButton
                  size="small"
                  onClick={() => handleZoneInfoClick(savedZoneSets[zoneKeys[0]])}
                >
                  <InfoIcon />
                </IconButton>
              )}
            </Box>
            <Box display="flex" pt={1.5} justifyItems="start" width="100%">
              <SelectComponent
                selectedZone={selectedZone}
                onChange={onChange}
                options={options}
                hasMultipleZoneSets={hasMultipleZoneSets}
                savedZoneSets={savedZoneSets}
                handleZoneInfoClick={handleZoneInfoClick}
                zoneKeys={zoneKeys}
              />
            </Box>
          </>
          <Box pt={3} width="100%" mt={1} maxWidth="sm" mb={3}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => {
                handleSave();
                onClose && onClose();
              }}
              disabled={!selectedZone}
            >
              <Trans>Save</Trans>
            </Button>
          </Box>
        </Box>
      </ErrorBoundary>
    </Container>
  );
};

export default PowerZoneUpdate;
